// Issue Overview Component
// Dashboard for issue management overview and metrics

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { issueService } from '@/services/issueService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface IssueOverviewProps {
  onOverviewData?: (data: any | null) => void;
}

export const IssueOverview: React.FC<IssueOverviewProps> = ({ onOverviewData }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const issues = await issueService.getIssues();
      
      // Calculate statistics
      const stats = {
        total: issues.length,
        open: issues.filter((i: any) => i.status === 'OPEN').length,
        inProgress: issues.filter((i: any) => i.status === 'IN_PROGRESS').length,
        resolved: issues.filter((i: any) => i.status === 'RESOLVED').length,
        closed: issues.filter((i: any) => i.status === 'CLOSED').length,
        critical: issues.filter((i: any) => i.urgency === 'CRITICAL').length,
        high: issues.filter((i: any) => i.urgency === 'HIGH').length,
        medium: issues.filter((i: any) => i.urgency === 'MEDIUM').length,
        low: issues.filter((i: any) => i.urgency === 'LOW').length,
        byCategory: {} as Record<string, number>,
        recentIssues: issues
          .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 5)
      };

      // Count by category
      issues.forEach((issue: any) => {
        const category = issue.category_name || 'Uncategorized';
        stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
      });

      setOverview(stats);
      onOverviewData?.(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load issue overview');
      onOverviewData?.(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (!overview) {
    return null;
  }

  return (
    <div className="issue-overview space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="outline" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.total') || 'Total Issues'}</p>
                <p className="text-2xl font-bold text-[var(--color-surface-text)]">{overview.total}</p>
              </div>
              <ExclamationTriangleIcon className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card variant="outline" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.open') || 'Open'}</p>
                <p className="text-2xl font-bold text-blue-600">{overview.open}</p>
              </div>
              <ClockIcon className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card variant="outline" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.inProgress') || 'In Progress'}</p>
                <p className="text-2xl font-bold text-yellow-600">{overview.inProgress}</p>
              </div>
              <ClockIcon className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card variant="outline" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.resolved') || 'Resolved'}</p>
                <p className="text-2xl font-bold text-green-600">{overview.resolved}</p>
              </div>
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Urgency Breakdown */}
      <Card variant="outline" padding="md">
        <CardHeader>
          <CardTitle>{t('issues.urgencyBreakdown') || 'Urgency Breakdown'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{overview.critical}</p>
              <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.critical') || 'Critical'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{overview.high}</p>
              <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.high') || 'High'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{overview.medium}</p>
              <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.medium') || 'Medium'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{overview.low}</p>
              <p className="text-sm text-[var(--color-secondary-text)]">{t('issues.low') || 'Low'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      {Object.keys(overview.byCategory).length > 0 && (
        <Card variant="outline" padding="md">
          <CardHeader>
            <CardTitle>{t('issues.categoryBreakdown') || 'Category Breakdown'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(overview.byCategory)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="text-[var(--color-surface-text)]">{category}</span>
                    <span className="font-semibold text-[var(--color-primary-bg)]">{count as number}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Issues */}
      {overview.recentIssues.length > 0 && (
        <Card variant="outline" padding="md">
          <CardHeader>
            <CardTitle>{t('issues.recentIssues') || 'Recent Issues'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overview.recentIssues.map((issue: any) => (
                <div
                  key={issue.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/issues/${issue.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/issues/${issue.id}`); } }}
                  className="flex items-center justify-between border-b border-[var(--color-border)] pb-2 cursor-pointer hover:bg-[var(--color-secondary-bg)] rounded-md px-2 py-1 -mx-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-text)]"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[var(--color-surface-text)] truncate">{issue.title}</p>
                    <p className="text-sm text-[var(--color-secondary-text)]">
                      {new Date(issue.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ml-2 ${
                    issue.status === 'OPEN' ? 'bg-blue-100 text-blue-800' :
                    issue.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                    issue.status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {issue.status}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

